import { useFlags } from 'launchdarkly-react-client-sdk'
import { observer } from 'mobx-react-lite'
import React, { FC, useMemo } from 'react'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { useAuth } from '../../../hooks/useAuth'
import { styled } from '../../../lib/stitches'
import { useArpMedias } from '../../../queries/arpMedia'
import {
  groupSamplesByArtist,
  groupSamplesByCategory
} from '../../../stores/song/sample/SampleUtils'
import { Box, Column, Row, StyledScrollable } from '../../core/Components'
import { getSampleCategoryColor, gray12, gray7 } from '../../platform/color'
import ProfileAvatar from '../../platform/explore/common/ProfileAvatar'
import { segmentStudioButton } from '../StudioUtils'
import Search from '../controls/Search'
import { StudioTab } from '../controls/common'
import { MintSoundModal } from '../mint/upload-sound-modal/MintSoundModal'
import SampleList from './sample-list'

const StyledUploadSoundsButton = styled('button', {
  all: 'unset',
  background: 'linear-gradient(93.07deg, #8BACFF -3.71%, #C2ADFC 107.34%)',
  borderRadius: '8px',
  fontSize: '14px',
  lineHeight: '16px',
  fontWeight: '$medium',
  height: '48px',
  width: '100%',
  color: 'black',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
})

const UploadSoundsButton: FC = () => {
  const flags = useFlags()
  if (!flags.uploadSoundsModal) return null
  return (
    <MintSoundModal
      trigger={
        <StyledUploadSoundsButton
          onClick={() => {
            segmentStudioButton('Upload Sounds', 'sample library side drawer')
          }}
        >
          Upload & Mint Sounds
        </StyledUploadSoundsButton>
      }
    />
  )
}

const flattenSamplesByCategory = (samplesByCategory) => {
  let storedSampleIDs = new Set()
  let samples = []

  Object.keys(samplesByCategory).forEach((category) => {
    if (Object.prototype.toString.call(samplesByCategory[category]) === '[object Array]') {
      samplesByCategory[category].forEach((sample: IArpMedia) => {
        if (storedSampleIDs.has(sample.id)) return

        samples.push([sample, category])
        storedSampleIDs.add(sample.id)
      })
    } else {
      // for nested category types (drums)
      let nestedSamples = samplesByCategory[category]
      Object.keys(nestedSamples).forEach(
        (nestedCategory) =>
          Object.prototype.toString.call(nestedSamples[nestedCategory]) === '[object Array]' &&
          nestedSamples[nestedCategory].forEach((nestedSample: IArpMedia) => {
            if (storedSampleIDs.has(nestedSample.id)) return

            samples.push([nestedSample, nestedCategory])
            storedSampleIDs.add(nestedSample.id)
          })
      )
    }
  })

  return samples
}

const SampleSearchResults = ({ searchResults }) => (
  <Column>
    <SampleList samples={searchResults} defaultIsOpen={true} defaultIsActive={true} />
  </Column>
)

const SampleCategoryDropdown = ({ title, categoryGrouping }) => {
  return (
    <Box
      style={{ marginLeft: '10px', paddingLeft: '25px', borderLeft: `1px solid ${gray12}` }}
      unselectable
    >
      <SampleList
        samples={categoryGrouping}
        render={({ isOpen, toggle }) => (
          <a
            onClick={toggle}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '15px 0px 15px 0px',
              cursor: 'pointer',
              color: isOpen ? 'white' : gray7
            }}
          >
            <div>{title}</div>
            <img
              alt="Expand up/down"
              src={
                isOpen
                  ? '/static/icons/studio_chevron_down.svg'
                  : '/static/icons/studio_chevron_right.svg'
              }
            />
          </a>
        )}
      />
    </Box>
  )
}

type SamplesByCategory = { [key: string]: { [key: string]: IArpMedia[] } }

const colorMapSortOrder = {
  Drums: 1,
  Percussion: 2,
  SFX: 3,
  Vocals: 4,
  Synth: 5,
  Keys: 6,
  Guitar: 7,
  Bass: 8,
  Wind: 9,
  Misc: 10
}

const useInstruments = (samplesByCategory: SamplesByCategory) => {
  const categoriesWithColorsSorted = Object.keys(samplesByCategory)
    .map((category) => {
      const categoryName = (samplesByCategory[category].name || category) as string
      const color = getSampleCategoryColor(categoryName as string)

      return {
        category,
        color
      }
    })
    .sort((a, b) => colorMapSortOrder[a.category] - colorMapSortOrder[b.category])

  return { categoriesWithColorsSorted }
}

interface InstrumentsProps {
  samplesByCategory: SamplesByCategory
}

const Instruments: React.FC<InstrumentsProps> = ({ samplesByCategory }) => {
  const { categoriesWithColorsSorted } = useInstruments(samplesByCategory)

  return (
    <>
      {categoriesWithColorsSorted.map(({ category, color }) => {
        return (
          <SampleCategoryDropdownLarge
            category={category}
            samplesByCategory={samplesByCategory}
            key={category}
            color={color}
          />
        )
      })}
    </>
  )
}

interface PacksProps {
  samplesByArtist: SamplesByCategory
}

const Packs: React.FC<PacksProps> = ({ samplesByArtist }) => {
  const artistEthAddresses: string[] = Object.keys(samplesByArtist).map((artist) => {
    const artistSamples = samplesByArtist[artist]
    return artistSamples[Object.keys(artistSamples)[0]][0].user.walletAddress
  })

  return (
    <>
      {Object.keys(samplesByArtist).map((artist, index) => (
        <SampleByArtistDropdown
          title={artist} // Use Artist name, since it looks better
          categoryGroupings={samplesByArtist[artist]}
          key={index}
        />
      ))}
    </>
  )
}

const SampleByArtistDropdown: FC<{
  title: string
  categoryGroupings: { [key: string]: IArpMedia[] }
}> = ({ title, categoryGroupings }) => {
  const artistEth = Object.values(categoryGroupings)[0][0].user.walletAddress
  return (
    <DropdownLarge
      title={title}
      eth={artistEth}
      child={Object.keys(categoryGroupings).map(
        (category) =>
          category !== 'name' && (
            <SampleCategoryDropdown
              title={category}
              categoryGrouping={categoryGroupings[category]}
              key={category}
            />
          )
      )}
    />
  )
}

const SamplePackDropdown = ({ title, categoryGroupings, color }) => (
  <DropdownLarge
    color={color}
    title={title}
    child={Object.keys(categoryGroupings)
      .sort((a, b) => a.localeCompare(b))
      .map(
        (category) =>
          category !== 'name' && (
            <SampleCategoryDropdown
              title={category}
              categoryGrouping={categoryGroupings[category]}
              key={category}
            />
          )
      )}
  />
)

const SampleCategoryDropdownLarge = ({ category, samplesByCategory, color }) => {
  if (Object.prototype.toString.call(samplesByCategory[category]) === '[object Array]')
    return (
      <DropdownLarge
        title={category}
        child={<SampleList samples={samplesByCategory[category]} defaultIsOpen={true} />}
        color={color}
      />
    )

  // If the given object is not an array, it has nested arrays for subcategories (e.g. Drums)
  return (
    <SamplePackDropdown
      title={samplesByCategory[category].name}
      categoryGroupings={samplesByCategory[category]}
      key={samplesByCategory[category].name}
      color={color}
    />
  )
}

interface DropdownLargeProps {
  title: string
  child: React.ReactElement | React.ReactElement[]
  color?: string
  eth?: string
}

const DropdownLarge: React.FC<DropdownLargeProps> = ({ title, child, color, eth = '' }) => {
  const [isShowing, setIsShowing] = React.useState(false)

  return (
    <Box unselectable>
      <a
        className="w-100 cursor-pointer d-inline-flex align-items-center justify-content-between"
        onClick={() => setIsShowing(!isShowing)}
        style={{ height: '50px' }}
      >
        <Row verticalCenter>
          <Box css={{ marginRight: '12px' }}>
            {color ? (
              <div
                style={{
                  minWidth: '20px',
                  maxWidth: '20px',
                  height: '16px',
                  borderRadius: '4px',
                  backgroundColor: color
                }}
              />
            ) : (
              <ProfileAvatar eth={eth} />
            )}
          </Box>
          <span>{title}</span>
        </Row>
        <img
          src={
            isShowing
              ? '/static/icons/studio_chevron_down.svg'
              : '/static/icons/studio_chevron_right.svg'
          }
        />
      </a>
      <ul className="ms-0 ps-0" style={{ listStyleType: 'none', display: !isShowing && 'none' }}>
        {child}
      </ul>
    </Box>
  )
}

const useSamplesByCategory = (defaultSamplesOnly: boolean = false) => {
  // TODO Paginate this
  const defaultSamplesQuery = useArpMedias({
    isDefaultSample: true,
    perPage: 100,
    isBlocklist: false
  })
  const _ids =
    defaultSamplesQuery.data?.pages.flatMap((page) => page.data.map((sample) => sample?.id)) || []
  const ids = _ids.filter((id) => !!id)

  const samplesByCategoryRaw = useMemo(() => {
    const seenArpIDs = new Set()
    const filteredMedia = []

    const media = defaultSamplesOnly
      ? ids.map((id) => ArpRegistry.retrieveFromCache(id))
      : [...ArpRegistry.getAllSamples(), ...ArpRegistry.getUploadedStemsReactive()]
    media.forEach((arpMediaItem) => {
      if (!arpMediaItem?.id) return
      if (seenArpIDs.has(arpMediaItem.id) || arpMediaItem.isBlocklist) return

      filteredMedia.push(arpMediaItem)
      seenArpIDs.add(arpMediaItem.id)
    })

    return groupSamplesByCategory(filteredMedia)
  }, [
    ArpRegistry.getAllSamples(),
    defaultSamplesOnly,
    ids,
    defaultSamplesOnly ? [] : ArpRegistry.getUploadedStemsReactive()
  ])

  let samplesByCategory = useMemo(() => {
    if (!samplesByCategoryRaw) return {}
    let organizedSamplesByCategory = {}
    Object.keys(samplesByCategoryRaw).forEach((category) => {
      // anything organized as a dictionary already will be added
      if (Object.prototype.toString.call(samplesByCategoryRaw[category]) !== '[object Array]') {
        organizedSamplesByCategory[category] = samplesByCategoryRaw[category]
        return
      }
      // these are arrays we need to sort by subcategory
      let subCategories = { name: category }
      samplesByCategoryRaw[category].forEach((instrument) => {
        const category = instrument.category
        subCategories[category]
          ? subCategories[category].push(instrument)
          : (subCategories[category] = [instrument])
      })
      // if the only category is the same as the parent category name, no reason to organize like this
      if (Object.keys(subCategories).length === 2 && Object.keys(subCategories).includes(category))
        organizedSamplesByCategory[category] = samplesByCategoryRaw[category]
      else organizedSamplesByCategory[category] = subCategories
    })
    return organizedSamplesByCategory
  }, [samplesByCategoryRaw])

  return { samplesByCategory }
}

const SampleSelector = observer(() => {
  const [selectedTab, setSelectedTab] = React.useState(0)
  const [query, setQuery] = React.useState('')
  const { user } = useAuth()
  const { samplesByCategory } = useSamplesByCategory()
  const { samplesByCategory: defaultSamplesByCategory } = useSamplesByCategory(true)

  const samplesByArtist = useMemo(
    () =>
      groupSamplesByArtist(
        [...ArpRegistry.getAllSamples(), ...ArpRegistry.getUploadedStemsReactive()].filter(
          (media) => !media.isBlocklist
        )
      ),
    [ArpRegistry.getAllSamples(), ArpRegistry.getUploadedStemsReactive()]
  )

  const searchResults = useMemo(
    () =>
      flattenSamplesByCategory(samplesByCategory)
        .filter(
          ([sample, sampleCategory]: [IArpMedia, string]) =>
            sample?.metadata?.title?.toLowerCase().includes(query.toLowerCase()) ||
            sampleCategory?.toLowerCase().includes(query.toLowerCase()) ||
            sample?.category?.toLowerCase().includes(query.toLowerCase())
        )
        .map(([sample]) => sample),
    [samplesByCategory, query]
  )

  const maxHeight = '75vh'
  return (
    <Column
      className="studio-text-sm"
      style={{
        width: '100%',
        height: '100%',
        minHeight: '100%',
        overflow: 'hidden'
      }}
      alignItemsStretch
    >
      <p className="fs-16 fw-medium">Sample Library</p>
      <Search
        query={query}
        placeholder="Search for samples"
        setQuery={setQuery}
        style={{ marginTop: '0', marginBottom: '10px' }}
      />
      {query === '' ? (
        <>
          <Row>
            <StudioTab
              active={selectedTab === 0}
              onClick={() => {
                segmentStudioButton('View Default Samples', 'sample library side drawer')
                setSelectedTab(0)
              }}
            >
              Default
            </StudioTab>
            <StudioTab
              active={selectedTab === 1}
              onClick={() => {
                segmentStudioButton('View Global Samples', 'sample library side drawer')
                setSelectedTab(1)
              }}
            >
              Global
            </StudioTab>
            {user && (
              <StudioTab
                active={selectedTab === 2}
                onClick={() => {
                  segmentStudioButton('View My Uploaded Samples', 'sample library side drawer')
                  setSelectedTab(2)
                }}
              >
                My Uploads
              </StudioTab>
            )}
          </Row>
          {ArpRegistry.readyForStudioUse && (
            <StyledScrollable css={{ flex: 1, fontSize: '14px', paddingTop: '0.25rem' }}>
              {selectedTab === 0 && <Instruments samplesByCategory={defaultSamplesByCategory} />}
              {selectedTab === 1 && <Instruments samplesByCategory={samplesByCategory} />}
              {selectedTab === 2 && (
                <Instruments samplesByCategory={samplesByArtist[user.display_name] || {}} />
              )}
            </StyledScrollable>
          )}
        </>
      ) : (
        <StyledScrollable css={{ height: maxHeight, maxHeight: maxHeight }}>
          {ArpRegistry.readyForStudioUse && <SampleSearchResults searchResults={searchResults} />}
        </StyledScrollable>
      )}
      <UploadSoundsButton />
    </Column>
  )
})

export default SampleSelector
